import {
  type Activatable,
  type ActivationLifecycle,
  type Deactivatable,
} from '@/infra/activation'
import { useLayoutEffect, useRef } from 'react'
import { isAbortError } from '@/utils/abort-util'
import type { RootStore } from '@/RootStore'
import {
  needsImpl,
  type SelectedFeatures,
  useRootStore,
} from '@/contexts/root-store-context'
import { shallowItemsAreEqual } from '@/utils/array-util'

const EMPTY: readonly unknown[] = []

/**
 * A function that can be called once with promise props inside
 * of {@link useScopedViewModel} to suspend rendering until
 * the promises resolve.
 */
export type Needs = <T extends Record<string, unknown>>(
  props: T,
) => SelectedFeatures<T>

/* eslint-disable react-hooks/refs -- The view model and the Suspense bookkeeping are deliberately kept in refs that are read and written during render. */
/**
 * Uses the specified {@see create} function to create a view model
 * that will live as long as the component that uses it.
 *
 * This is useful for view models that do not influence, and are not influenced
 * by parent view models.
 *
 * @param create
 * @param deps
 */
export function useScopedViewModel<T>(
  create: (rootStore: RootStore, needs: Needs) => T,
  deps?: readonly unknown[],
): T {
  // We'll pass in the root store to the create function so it can
  // pluck what's needed.
  const rootStore = useRootStore()
  const vmRef = useRef<T>(null)
  const depsRef = useRef<readonly unknown[]>(deps)
  const promiseRef = useRef<Promise<unknown> | null>(null)
  const previousPromiseRef = useRef<Promise<unknown> | null>(null)

  // We only want to instantiate a view model once per component,
  // or if the deps change.
  if (
    !vmRef.current ||
    !shallowItemsAreEqual(depsRef.current ?? EMPTY, deps ?? EMPTY)
  ) {
    vmRef.current = create(rootStore, (props) => {
      if (
        previousPromiseRef.current &&
        previousPromiseRef.current === promiseRef.current
      ) {
        throw new Error(
          'Calling the `needs` function passed to `useScopedViewModel` more than once is not allowed',
        )
      }

      const result = needsImpl(props, promiseRef)
      previousPromiseRef.current = promiseRef.current
      return result
    })

    promiseRef.current = null
    previousPromiseRef.current = null
    depsRef.current = deps
  }

  return vmRef.current
}
/* eslint-enable react-hooks/refs */

/**
 * Activates the view model on mount and when the
 * {@link activate} method (if specified) changes.
 * If the {@link activatable} implements a `deactivate` method, it will
 * be called during cleanup.
 *
 * @param activatable
 */
export function useActivation<
  T extends Activatable | Deactivatable | undefined,
>(activatable: T): unknown
/**
 * Activates the view model on mount and when the
 * {@link activate} method (if specified) changes.
 * If the {@link activatable} implements a `deactivate` method, it will
 * be called during cleanup.
 *
 * @param activatable
 * @param activate
 */
export function useActivation<T extends Deactivatable | undefined>(
  activatable: T,
  activate: (activatable: T) => unknown,
): unknown
/**
 * Activates the view model on mount and when the
 * {@link activate} method (if specified) changes.
 * If the {@link activatable} implements a `deactivate` method, it will
 * be called during cleanup.
 *
 * @param activatable
 * @param activate
 */
export function useActivation<
  T extends Partial<ActivationLifecycle> | undefined,
>(activatable: T, activate?: (activatable: T) => unknown) {
  useLayoutEffect(() => {
    void executeActivation()

    // If the activatable supports deactivation, then deactivate
    // it on effect cleanup.
    if (isDeactivatable(activatable)) {
      return () => {
        activatable.deactivate()
      }
    }

    async function executeActivation() {
      try {
        if (activate) {
          return await activate(activatable)
        }

        if (isActivatable(activatable)) {
          return await activatable.activate()
        }
      } catch (err: unknown) {
        // Ignore abort errors.
        if (isAbortError(err)) {
          return
        }

        throw err
      }
    }
  }, [activatable, activate])
}

function isActivatable(
  value: Partial<ActivationLifecycle> | undefined,
): value is Activatable {
  return value !== undefined && 'activate' in value
}

function isDeactivatable(
  value: Partial<ActivationLifecycle> | undefined,
): value is Deactivatable {
  return value !== undefined && 'deactivate' in value
}
